import { useEffect, useRef, useState, useCallback } from 'react'
import type { ActivityEvent } from '@/types/dashboard'
import { useAuthStore } from '@/store/authStore'

interface UseActivityStreamOptions {
  maxEvents?: number
  autoConnect?: boolean
}

interface UseActivityStreamResult {
  events: ActivityEvent[]
  connected: boolean
  error: string | null
  connect: () => void
  disconnect: () => void
  clearEvents: () => void
}

/**
 * Custom hook for connecting to the activity feed SSE stream.
 * Falls back to polling if SSE is not available.
 */
export function useActivityStream(
  options: UseActivityStreamOptions = {}
): UseActivityStreamResult {
  const { maxEvents = 100, autoConnect = true } = options
  const [events, setEvents] = useState<ActivityEvent[]>([])
  const [connected, setConnected] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const eventSourceRef = useRef<EventSource | null>(null)
  const eventIdCounter = useRef(0)
  const token = useAuthStore((state) => state.token)

  const addEvent = useCallback(
    (event: Omit<ActivityEvent, 'id'>) => {
      setEvents((prev) => {
        const newEvent: ActivityEvent = {
          ...event,
          id: ++eventIdCounter.current,
        }
        const updated = [newEvent, ...prev]
        return updated.slice(0, maxEvents)
      })
    },
    [maxEvents]
  )

  const connect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
    }

    if (!token) {
      setError('Not authenticated')
      return
    }

    try {
      // Connect to SSE endpoint with token
      const url = new URL('/api/v1/chat/events', window.location.origin)
      url.searchParams.set('token', token)
      url.searchParams.set('types', 'tool_call,feedback,error,session')

      const eventSource = new EventSource(url.toString())
      eventSourceRef.current = eventSource

      eventSource.onopen = () => {
        setConnected(true)
        setError(null)
      }

      eventSource.onerror = () => {
        setConnected(false)
        setError('Connection lost. Reconnecting...')
        // EventSource will auto-reconnect
      }

      eventSource.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data)

          // Handle different event types
          if (data.type === 'tool_call') {
            addEvent({
              timestamp: data.timestamp || new Date().toISOString(),
              type: 'tool_call',
              toolName: data.tool_name,
              success: data.success,
              durationMs: data.duration_ms,
              sessionId: data.session_id,
              description: data.description || `Called ${data.tool_name}`,
            })
          } else if (data.type === 'feedback') {
            addEvent({
              timestamp: data.timestamp || new Date().toISOString(),
              type: 'feedback',
              toolName: data.tool_name,
              description:
                data.rating > 0
                  ? `Positive feedback for ${data.tool_name}`
                  : `Negative feedback for ${data.tool_name}`,
            })
          } else if (data.type === 'error') {
            addEvent({
              timestamp: data.timestamp || new Date().toISOString(),
              type: 'error',
              toolName: data.tool_name,
              success: false,
              description: data.error || 'An error occurred',
            })
          } else if (data.type === 'session') {
            addEvent({
              timestamp: data.timestamp || new Date().toISOString(),
              type: 'session',
              sessionId: data.session_id,
              description: data.description || 'Session event',
            })
          }
        } catch (parseError) {
          console.error('Failed to parse SSE event:', parseError)
        }
      }

      // Handle specific event types if the server sends named events
      eventSource.addEventListener('tool_call', (e) => {
        try {
          const data = JSON.parse(e.data)
          addEvent({
            timestamp: data.timestamp || new Date().toISOString(),
            type: 'tool_call',
            toolName: data.tool_name,
            success: data.success,
            durationMs: data.duration_ms,
            sessionId: data.session_id,
            description: data.description || `Called ${data.tool_name}`,
          })
        } catch {
          // ignore parse errors
        }
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to connect')
      setConnected(false)
    }
  }, [token, addEvent])

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
    setConnected(false)
  }, [])

  const clearEvents = useCallback(() => {
    setEvents([])
  }, [])

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect && token) {
      connect()
    }

    return () => {
      disconnect()
    }
  }, [autoConnect, token, connect, disconnect])

  return {
    events,
    connected,
    error,
    connect,
    disconnect,
    clearEvents,
  }
}

/**
 * Fallback polling hook when SSE is not available.
 */
export function useActivityPolling(intervalMs = 5000): UseActivityStreamResult {
  const [events, setEvents] = useState<ActivityEvent[]>([])
  const [connected, setConnected] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const token = useAuthStore((state) => state.token)

  const fetchEvents = useCallback(async () => {
    if (!token) {
      setError('Not authenticated')
      return
    }

    try {
      const response = await fetch('/api/v1/activity?limit=50', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const data = await response.json()
      if (data.ok && data.events) {
        setEvents(data.events)
        setConnected(true)
        setError(null)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch')
      setConnected(false)
    }
  }, [token])

  const connect = useCallback(() => {
    fetchEvents()
    intervalRef.current = setInterval(fetchEvents, intervalMs)
  }, [fetchEvents, intervalMs])

  const disconnect = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    setConnected(false)
  }, [])

  const clearEvents = useCallback(() => {
    setEvents([])
  }, [])

  useEffect(() => {
    if (token) {
      connect()
    }

    return () => {
      disconnect()
    }
  }, [token, connect, disconnect])

  return {
    events,
    connected,
    error,
    connect,
    disconnect,
    clearEvents,
  }
}
